import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AlertCircle, ChevronRight, Check, X } from 'lucide-react';
import { theme } from '../../../lib/theme';
import { useTranslations } from '../../../i18n';
import { usePreferences } from '../../../contexts/PreferencesContext';
import { useJarvis } from './JarvisProvider';

// Import the REAL components used in Dashboard
import { LogEntryItem } from '../../cars/LogEntryItem';
import { NoteItem } from '../../cars/NoteItem';
import { SparePartItem } from '../../cars/SparePartItem';
import { LocationItem } from '../../cars/LocationItem';

interface Message {
    id: string;
    role: 'user' | 'assistant' | 'system';
    content: string;
    payload?: any;
    created_at: string;
    is_read: boolean;
    is_internal?: boolean;
    error?: boolean;
}

export const JarvisMessage = React.memo(({ message, isLast }: { message: Message; isLast?: boolean }) => {
    const isAssistant = message.role === 'assistant';
    const { t } = useTranslations();
    const { preferences } = usePreferences();
    const { sendMessage, executeAction } = useJarvis();
    
    // Track local action statuses (confirmed, deleted) for this specific message instance
    const [actionStatuses, setActionStatuses] = React.useState<Record<string, 'confirmed' | 'deleted'>>({});

    // Safety parse for payload if it comes as string from DB
    const payload = typeof message.payload === 'string' ? JSON.parse(message.payload) : (message.payload || {});
    
    // Deduplicate links by ID to prevent React duplicate key warnings
    const rawLinks = payload.links || [];
    const links = Array.from(new Map(rawLinks.map((item: any) => [item.id || Math.random().toString(), item])).values());

    const handleAction = async (type: 'confirm' | 'delete', link: any) => {
        try {
            // Optimistically update UI
            setActionStatuses(prev => ({ ...prev, [link.id]: type === 'confirm' ? 'confirmed' : 'deleted' }));

            await executeAction({
                type,
                target_id: link.id,
                target_type: link.type || 'log',
                message_id: message.id
            });
        } catch (e) {
            console.error("Action failed", e);
            // Rollback on failure? For now just log.
        }
    };

    // Clean message content: 
    // 1. Hide everything after the |---DATA---| delimiter
    // 2. Fallback: Hide system tags [ACTION], [PROGRESS], [DECODE_VIN]
    let cleanContent = message.content.split('|---DATA---|')[0];
    
    cleanContent = cleanContent
        .replace(/\[ACTION:[\s\S]*?(\]|(?=$))/gi, '')   // Fallback: Remove ACTION tags
        .replace(/\[PROGRESS:[\s\S]*?(\]|(?=$))/gi, '') // Fallback: Remove PROGRESS tags
        .replace(/\[DECODE_VIN:[\s\S]*?(\]|(?=$))/gi, '') // Fallback: Remove VIN tags
        .replace(/\s*\[[A-Z]+\d+\]\s*/gi, ' ')         // Remove mentions [F1], [LOC1] etc.
        .replace(/\s+([.,!?;:])/g, '$1')              // Fix spaces before punctuation
        .replace(/\s\s+/g, ' ')                      // Double spaces to single
        .trim();

    const handleRetry = () => {
        if (message.error && !isAssistant) {
            sendMessage(message.content, message.id);
        }
    };

    // Construct effective preferences for LogEntryItem
    const effectivePreferences = preferences ? {
        ...preferences,
        distance_unit: preferences.distance_unit || 'km',
        volume_unit: preferences.volume_unit || 'liters',
        currency: preferences.currency || 'EUR',
        currency_symbol: preferences.currency_symbol || '€'
    } : null;

    const renderLink = (link: any) => {
        const slug = link.car_slug || 'garage';
        
        // Combine local state and persisted state from payload
        const status = actionStatuses[link.id] || (link.is_processed ? link.action_status : null);
        const isConfirmed = status === 'confirmed';
        const isDeleted = status === 'deleted';
        const isProcessed = isConfirmed || isDeleted;
        const isTemp = link.is_temporary && !isProcessed;

        const Content = () => {
            if (link.type === 'note') return <NoteItem note={link} slug={slug} compact={true} openInNewTab={true} />;
            if (link.type === 'part') return <SparePartItem part={link} slug={slug} compact={true} openInNewTab={true} />;
            if (link.type === 'location') return <LocationItem location={link} slug={slug} compact={true} />;
            return <LogEntryItem entry={link} slug={slug} effectivePreferences={effectivePreferences} openInNewTab={true} />;
        };

        return (
            <div key={link.id} className="relative flex items-stretch gap-2 mb-2 last:mb-0">
                <div className={`flex-1 bg-white dark:bg-white/5 rounded-xl border ${isTemp ? 'border-amber-500/40 bg-amber-50/30 dark:bg-amber-500/5' : (isDeleted ? 'border-red-500/20 bg-red-500/5' : 'border-gray-100 dark:border-white/10')} overflow-hidden transition-all duration-300`}>
                    <Content />
                    {(isTemp || isProcessed) && (
                        <div className={`px-4 py-1 border-t flex items-center justify-between ${isConfirmed ? 'bg-green-500/10 border-green-500/20' : (isDeleted ? 'bg-red-500/10 border-red-500/20' : 'bg-amber-500/10 border-amber-500/20')}`}>
                            <span className={`text-[9px] font-black uppercase tracking-widest ${isConfirmed ? 'text-green-600 dark:text-green-400' : (isDeleted ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400')}`}>
                                {isConfirmed ? 'Izveidots' : (isDeleted ? 'Noraidīts' : 'Pagaidu ieraksts')}
                            </span>
                        </div>
                    )}
                </div>
                
                {isTemp && (
                    <div className="flex flex-col gap-1.5 w-[60px]">
                        <button 
                            onClick={() => handleAction('confirm', link)}
                            className="flex-1 bg-green-500 hover:bg-green-600 text-white rounded-xl shadow-lg shadow-green-500/10 flex items-center justify-center transition-all active:scale-95 group"
                            title="Apstiprināt"
                        >
                            <Check size={28} strokeWidth={3} className="group-hover:scale-110 transition-transform" />
                        </button>
                        <button 
                            onClick={() => handleAction('delete', link)}
                            className="flex-1 bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white rounded-xl border border-red-500/20 hover:border-red-500 flex items-center justify-center transition-all active:scale-95 group"
                            title="Noraidīt"
                        >
                            <X size={28} strokeWidth={3} className="group-hover:scale-110 transition-transform" />
                        </button>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className={`flex flex-col ${isAssistant ? 'items-start' : 'items-end'} mb-6 px-4 w-full`}>
            <div className="flex items-start gap-2 max-w-[95%] w-full flex-col">
                <div 
                    className={`rounded-2xl px-4 py-3 shadow-sm ${
                        isAssistant 
                            ? `${theme.colors.cardBg} ${theme.colors.border} border text-gray-900 dark:text-white rounded-tl-none` 
                            : `${theme.colors.primaryBg} text-white rounded-tr-none ${message.error ? 'opacity-70' : ''}`
                    } w-fit self-${isAssistant ? 'start' : 'end'}`}
                >
                    <div className="prose prose-sm dark:prose-invert max-w-none text-inherit leading-relaxed">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {cleanContent}
                        </ReactMarkdown>
                    </div>

                    {/* DEBUG OUTPUT - Condensed */}
                    {(payload.debug_response || payload.debug_sent) && (
                        <details className="mt-2 group cursor-pointer">
                            <summary className="text-[7px] uppercase tracking-widest text-gray-400 font-bold hover:text-blue-500 transition-colors list-none flex items-center gap-1">
                                <ChevronRight size={8} className="group-open:rotate-90 transition-transform" />
                                Debug Info
                            </summary>
                            <div className="mt-1 p-2 bg-black/10 dark:bg-black/40 rounded text-[8px] opacity-40 font-mono border border-black/5 dark:border-white/5 overflow-x-auto">
                                <div className="max-h-[80px] overflow-y-auto whitespace-pre-wrap break-all leading-tight">
                                    {isAssistant ? payload.debug_response : payload.debug_sent}
                                </div>
                            </div>
                        </details>
                    )}

                    <p className={`text-[9px] mt-2 opacity-40 ${isAssistant ? 'text-left' : 'text-right'}`}>
                        {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                </div>

                {/* LINKS SECTION - Using real dashboard components */}
                {isAssistant && links.length > 0 && (
                    <div className="mt-3 flex flex-col gap-1 w-full max-w-[500px]">
                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 flex items-center gap-2 px-1">
                            {t.dashboard.recentExpenses || 'Pieminētie ieraksti'}
                        </div>
                        <div className="space-y-3 mt-4">
                            {links.map((link: any, idx: number) => (
                                <div key={`${message.id}-${link.id || idx}`} className="animate-in fade-in slide-in-from-bottom-2 duration-300 scale-90 origin-top-left w-[111.11%]">
                                    {renderLink(link)}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Error Icon */}
                {message.error && !isAssistant && (
                    <button
                        onClick={handleRetry}
                        className="flex-shrink-0 mt-2 w-8 h-8 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg transition-all active:scale-95 self-end"
                    >
                        <AlertCircle size={18} />
                    </button>
                )}
            </div>
        </div>
    );
});
